import { useEffect, useMemo, useRef, useState } from 'react';
import { api, darf, datum, type Anmelder } from '../api.ts';
import {
  Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import {
  zerlege, fettTeile, SCHRITTE_UEBERSCHRIFT,
} from '../teile/handbuchmarken.ts';
import { useFrisch } from '../teile/Frisch.tsx';

// ============================================================== Das Handbuch
//
// ORIGINALTEXT MARVIN (27.08.2026)
//   „das handbuch kannst du raus als richtigen menüpunkt machen und fülle es
//    komplett mit inhalt zu der neuen plattform mit echten bildern, wo man
//    klicken muss und ablaufplänen. sauber schrieben nicht so monotoner text
//    einfach in masse runter geschrieben. man soll es verstehen."
//
// ES LAG ALS REITER HINTER „KONFIGURATION", also hinter einem Bildschirm, auf
// dem man Schlüssel und Werte pflegt. Wer eine Anleitung sucht, sucht sie
// nicht dort - und wer kein Recht auf die Konfiguration hat, hatte sie GAR
// NICHT. Das betraf jeden Monteur und jede Partnerbauleitung, also genau die
// Leute, für die die Hälfte der Kapitel geschrieben ist.
//
// WAS AN DEN ALTEN KAPITELN MONOTON WAR, war nicht die Länge, sondern die
// Bauart: 24 Blöcke Fliesstext mit Grossbuchstaben als einzigem
// Gliederungsmittel, kein Bild, keine Reihenfolge. Drei Sachen sind deshalb
// neu, und alle drei stecken in DIESER Datei:
//
//   1. EINE GLIEDERUNG, die sich auch von Hand tippen lässt. „## " ist eine
//      Zwischenüberschrift, „• " ein Aufzählungspunkt, „> " ein Merksatz.
//      Drei Zeichen, mehr braucht es nicht - wer im Betrieb ein Kapitel
//      nachträgt, soll dafür keine Auszeichnungssprache lernen müssen.
//   2. ECHTE BILDSCHIRMFOTOS, als Marke `[bild: datei.png | Unterschrift]`.
//      Sie liegen im Bündel unter `handbuch/` und entstehen mit
//      `web/werkzeug/handbuchbilder.mjs`, das die Anwendung wirklich aufmacht,
//      einen Ring an die Stelle malt, an der geklickt wird, und fotografiert.
//   3. ABLAUFPLÄNE, als Marke `[ablauf: schluessel]`. Sie stehen HIER im
//      Programmcode und nicht in der Datenbank: ein Ablauf ist eine Aussage
//      über das System und keine, die sich beim Nachtragen eines Satzes
//      ändert.
//
// DIE PLÄNE SIND KEINE SVG-ZEICHNUNG, SONDERN KÄSTEN UND PFEILE AUS CSS. Eine
// gezeichnete Grafik hat eine feste Breite; auf 375 Pixeln ist sie entweder
// unlesbar klein oder ragt hinaus. Kästen brechen um.

// ---------------------------------------------------------------- Ablaufpläne

type Schritt = {
  wer?: string;
  was: string;
  dazu?: string;
  /** Zwei Ausgänge statt eines Pfeils. Der erste ist der Regelfall. */
  zweig?: { wenn: string; dann: string }[];
};

const ABLAEUFE: Record<string, { titel: string; schritte: Schritt[] }> = {

  auftrag: {
    titel: 'Der Weg eines Auftrags',
    schritte: [
      { wer: 'Telekom', was: 'Abruf kommt herein',
        dazu: 'Als Datei im Posteingang. Daraus entsteht der Auftrag.' },
      { wer: 'Innendienst', was: 'Auftrag vorbereiten',
        dazu: 'Lageplan zeichnen, Angaben für AAZ und VAO erfassen.' },
      { wer: 'Innendienst', was: 'Behördenpapiere',
        dazu: 'Aufgrabungsanzeige und Antrag auf Anordnung.' },
      { wer: 'Innendienst', was: 'Termin vergeben',
        dazu: 'Firma und Tag. Erst hier steht fest, wer baut.' },
      { wer: 'Partnerfirma', was: 'Bauen und hochladen',
        dazu: 'Fotos in die Pflichtordner, Aufmaß erfassen.' },
      { wer: 'Partnerfirma', was: 'Fertig melden',
        dazu: 'Der Knopf sagt zuerst, was noch fehlt.' },
      { wer: 'Innendienst', was: 'Aufmaß prüfen',
        dazu: 'Mengen gegenhalten, Differenzen begründen.' },
      { wer: 'Innendienst', was: 'LEB und Rotberichtigung',
        dazu: 'Die CSV für eVergabe und die Nachweismappe.' },
    ],
  },

  behoerde: {
    titel: 'Vom Antrag bis zur Anordnung',
    schritte: [
      { wer: 'Innendienst', was: 'Vorbereiten',
        dazu: 'Ohne die Angaben lässt sich kein Papier erzeugen.' },
      { wer: 'Innendienst', was: 'Ansehen',
        dazu: 'Die Vorschau legt nichts ab. Ein falsches Exemplar bleibt so '
            + 'gar nicht erst in der Mappe stehen.' },
      { wer: 'Innendienst', was: 'Erzeugen',
        dazu: 'Jetzt liegt das Papier im Auftragsordner.' },
      { wer: 'Innendienst', was: 'Versenden',
        dazu: 'Ein zweiter Knopf. Solange der Testmodus läuft, geht nichts '
            + 'wirklich hinaus - der Versuch steht trotzdem im Protokoll.' },
      { wer: 'Gemeinde', was: 'Antwortet',
        dazu: 'Die Anordnung kommt per Mail zurück.' },
      { wer: 'System', was: 'Posteingang ordnet zu',
        dazu: 'Erkannt am Inhalt, nicht am Dateinamen.' },
      { wer: 'Innendienst', was: 'Eingang erfassen',
        dazu: 'Erst hier entstehen Frist, Aktenzeichen und Gebühr. Herkunft '
            + 'schlägt Inhalt: nur ein Papier von der Behörde trägt sie.' },
    ],
  },

  eingang: {
    titel: 'Was mit einer eingehenden Datei passiert',
    schritte: [
      { wer: 'n8n oder Mensch', was: 'Datei kommt an',
        dazu: 'Aus einem Postfach oder über die Abwurffläche.' },
      { wer: 'System', was: 'Art bestimmen',
        dazu: 'Am INHALT: Text der PDF, Kopfzeile der Tabelle, ZIP-Kopf. Ein '
            + 'Dateiname ist kein Beweis.' },
      { wer: 'System', was: 'Auftrag suchen',
        dazu: 'KLS-Nummer, dann Adresse. Die Hausnummer muss exakt passen.',
        zweig: [
          { wenn: 'sicher zugeordnet',
            dann: 'Ab in den richtigen Ordner des Auftrags. Fertig.' },
          { wenn: 'nicht sicher',
            dann: 'Liegt im Posteingang, MIT GRUND. Alle 5 Minuten sieht '
                + 'der Server noch einmal nach - eine Anordnung kommt oft vor '
                + 'ihrem Auftrag an.' },
        ] },
      { wer: 'Innendienst', was: 'Was übrig bleibt, von Hand',
        dazu: 'Die Zahl am Menüpunkt sagt, wie viele liegen.' },
    ],
  },

  rb: {
    titel: 'Die Nachweismappe entsteht',
    schritte: [
      { wer: 'Partnerfirma', was: 'Ordner füllen',
        dazu: 'Checkliste, Einblasprotokoll, Fotos.' },
      { wer: 'Innendienst', was: 'Art wählen',
        dazu: 'Komplett, Teilaufmaß oder Schlussaufmaß. Teil und Schluss '
            + 'ergeben zusammen genau das Komplette.' },
      { wer: 'System', was: 'Befund',
        dazu: 'Was fehlt, was entfällt, was sich nicht einbetten ließ. '
            + 'FEHLT ist nicht ENTFÄLLT.' },
      { wer: 'System', was: 'Mappe bauen',
        dazu: 'Bilder werden zu Seiten, geheftet wird in fester Reihenfolge.' },
      { wer: 'Innendienst', was: 'Abgeben',
        dazu: 'Die PDF liegt im Auftragsordner und ist an der Zeile gemerkt.',
        zweig: [
          { wenn: 'stimmt',
            dann: 'Fertig. Ein zweiter Lauf würde sie ersetzen.' },
          { wenn: 'zu viel drin',
            dann: 'Zurückziehen, mit Grund. Die Datei bleibt liegen, die '
                + 'Zeile trägt ab jetzt Datum, Person und Grund.' },
        ] },
    ],
  },

  onboarding: {
    titel: 'Eine Partnerfirma einrichten',
    schritte: [
      { wer: 'Stammdaten', was: 'Firma anlegen',
        dazu: 'Name und GEWERKE. Daran hängt, wen die Terminvergabe anbietet.' },
      { wer: 'Benutzer und Rechte', was: 'Zugang anlegen',
        dazu: 'Name und Mailadresse. Die Mailadresse IST der Anmeldename.' },
      { wer: 'Benutzer und Rechte', was: 'Firma zuweisen',
        dazu: 'Das ist die Sichtbarkeitsgrenze: ab jetzt sieht dieser Zugang '
            + 'nur Aufträge mit einem Gewerk dieser Firma.' },
      { wer: 'Benutzer und Rechte', was: 'Gruppe wählen',
        dazu: '„Partner Bauleitung" pflegt zusätzlich den '
            + 'Verfügbarkeitskalender, „Partner Monteur" baut.' },
      { wer: 'Benutzer und Rechte', was: 'Passwort setzen',
        dazu: 'Ohne Passwort kommt niemand herein. Es gibt keine Einladung '
            + 'und keinen Link.' },
    ],
  },
};

function Ablaufplan({ schluessel }: { schluessel: string }) {
  const a = ABLAEUFE[schluessel];
  /* EIN PLAN, DEN ES NICHT GIBT, WIRD GENANNT UND NICHT VERSCHLUCKT. Wer ein
     Kapitel schreibt und sich vertippt, sieht sonst nichts und hält die Marke
     für falsch geschrieben, ohne zu wissen, wie sie richtig hiesse. */
  if (!a) {
    return (
      <p className="hinweis klein warnung">
        Den Ablaufplan „{schluessel}" gibt es nicht. Vorhanden sind:{' '}
        {Object.keys(ABLAEUFE).join(', ')}.
      </p>
    );
  }
  return (
    <figure className="ablaufplan">
      <figcaption>{a.titel}</figcaption>
      <ol>
        {a.schritte.map((s, i) => (
          <li key={i}>
            <div className="ablaufkasten">
              <span className="ablaufnummer" aria-hidden="true">{i + 1}</span>
              <div className="wachsen">
                {s.wer && <span className="ablaufwer">{s.wer}</span>}
                <b>{s.was}</b>
                {s.dazu && <p className="klein leise">{s.dazu}</p>}
              </div>
            </div>
            {s.zweig && (
              <div className="ablaufzweige">
                {s.zweig.map((z, k) => (
                  <div key={k} className={'ablaufzweig' + (k === 0 ? ' ja' : '')}>
                    <b className="klein">{z.wenn}</b>
                    <p className="klein leise">{z.dann}</p>
                  </div>
                ))}
              </div>
            )}
          </li>
        ))}
      </ol>
    </figure>
  );
}

// ------------------------------------------------------------ Der Textleser
//
// DIE REGELN STEHEN IN `teile/handbuchmarken.ts`, OHNE JSX DANEBEN.
// Sie standen hier, also an einer Stelle, die ein Prüfstand nur mit einer
// Textsuche erreicht - und eine Textsuche misst, dass etwas dasteht, nicht,
// dass es stimmt. Genau daran hing Marvins Frage vom 05.09.2026, ob auf dem
// Bildschirm noch ein Stern steht: das lässt sich nicht suchen, das muss man
// ausrechnen. Hier bleibt, was zeichnet.

function Bild({ datei, unter }: { datei: string; unter: string }) {
  const [weg, setWeg] = useState(false);
  /* EIN FEHLENDES BILD SAGT ES. Ein kaputtes Bildsymbol mitten im Text sieht
     aus wie ein Fehler der Anwendung; hier steht, welche Datei fehlt und
     womit sie gemacht wird. */
  if (weg) {
    return (
      <p className="hinweis klein warnung">
        Das Bild „{datei}" fehlt im Bündel. Es entsteht mit
        {' '}<code>node web/werkzeug/handbuchbilder.mjs</code>.
      </p>
    );
  }
  return (
    <figure className="handbuchbild">
      <img src={`/handbuch/${datei}`} alt={unter || datei}
           loading="lazy" decoding="async" onError={() => setWeg(true)} />
      {unter && <figcaption>{unter}</figcaption>}
    </figure>
  );
}

/**
 * Fett zeichnen, was `fettTeile` als fett zurückgibt.
 *
 * Die REGEL steht in `teile/handbuchmarken.ts`; hier steht nur, wie sie
 * aussieht. Ein Text ohne Marke geht unverändert durch - dann entsteht gar
 * kein zusätzliches Element.
 */
export function fettSetzen(zeile: string): React.ReactNode {
  const teile = fettTeile(zeile);
  if (teile.length === 1) return teile[0].text;
  return teile.map((t, i) => (t.fett ? <b key={i}>{t.text}</b> : t.text));
}

export function Kapiteltext({ text }: { text: string }) {
  const stuecke = useMemo(() => zerlege(text), [text]);
  return (
    <div className="handbuchtext">
      {stuecke.map((s, i) => {
        /* EINE ANLEITUNG IST EINE FOLGE UND KEINE AUFZÄHLUNG.
           ORIGINALTEXT MARVIN (05.09.2026)
             „es ist keine anleitung"
           Die Punkte unter der Überschrift „So geht es" bekommen deshalb
           NUMMERN: sie sagen, was ZUERST kommt, und das ist der ganze
           Unterschied zu einem Strich davor. Es ist keine sechste Marke -
           gelesen wird die Überschrift, die ohnehin dasteht. */
        const vorher = stuecke[i - 1];
        const schritte = vorher?.art === 'ueberschrift'
          && vorher.text.toLowerCase().startsWith(SCHRITTE_UEBERSCHRIFT.toLowerCase());
        switch (s.art) {
          case 'ueberschrift': return <h3 key={i}>{fettSetzen(s.text)}</h3>;
          case 'merksatz':
            return <p key={i} className="merksatz">{fettSetzen(s.text)}</p>;
          case 'punkte':
            if (schritte) {
              return (
                <ol key={i} className="schrittliste">
                  {s.punkte.map((p, k) => <li key={k}>{fettSetzen(p)}</li>)}
                </ol>
              );
            }
            return (
              <ul key={i}>
                {s.punkte.map((p, k) => <li key={k}>{fettSetzen(p)}</li>)}
              </ul>
            );
          case 'bild':
            return <Bild key={i} datei={s.datei} unter={s.unter} />;
          case 'ablauf':
            return <Ablaufplan key={i} schluessel={s.schluessel} />;
          default:
            return <p key={i}>{fettSetzen(s.text)}</p>;
        }
      })}
    </div>
  );
}

// ------------------------------------------------------------------ Die Seite

export default function Handbuch({ ich }: { ich: Anmelder }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [wo, setWo] = useState<string>('');
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [bearbeite, setBearbeite] = useState<any>(null);
  const leib = useRef<HTMLDivElement>(null);
  const darfPflegen = darf(ich, 'handbuch.pflegen');

  const laden = () => api.handbuch()
    .then((z) => { setZeilen(z); if (!wo && z[0]) setWo(z[0].schluessel); })
    .catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  useFrisch(laden);

  const alle = zeilen ?? [];
  /* GESUCHT WIRD IM TITEL UND IM TEXT. „Wo lade ich das Logo hoch" steht in
     keinem Kapitelnamen und sehr wohl in einem Kapitel. */
  const q = suche.trim().toLowerCase();
  const treffer = q
    ? alle.filter((k) => (k.titel + ' ' + k.text).toLowerCase().includes(q))
    : alle;
  const kapitel = alle.find((k) => k.schluessel === wo)
                ?? treffer[0] ?? alle[0] ?? null;

  function hin(k: any) {
    setWo(k.schluessel);
    /* Auf dem Telefon steht die Kapitelliste ÜBER dem Text. Ohne diesen
       Sprung tippt man ein Kapitel an und sieht weiter die Liste. */
    leib.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <>
      <div className="kopf">
        <h1>Handbuch</h1>
        <div className="wachsen" />
        <input className="feld" type="search" placeholder="Im Handbuch suchen"
               autoComplete="off" value={suche}
               onChange={(e) => setSuche(e.target.value)}
               style={{ maxWidth: 260 }} />
      </div>

      {/* Der Leib gehört in `.inhalt`, sonst läuft er bis an die Kante.
          Warum das hier stand und wer es gemeldet hat: siehe
          `seiten/Vordrucke.tsx`. (27.08.2026) */}
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />

        <div className="handbuch">
          <nav className="handbuchliste" aria-label="Kapitel">
            {q && (
              <p className="klein leise" style={{ padding: '0 6px 6px' }}>
                {treffer.length === 0
                  ? 'Kein Kapitel enthält das.'
                  : `${treffer.length} von ${alle.length} Kapiteln`}
              </p>
            )}
            {treffer.map((k) => (
              <button key={k.schluessel} type="button"
                      aria-current={kapitel?.schluessel === k.schluessel}
                      onClick={() => hin(k)}>
                {k.titel}
              </button>
            ))}
            {zeilen === null && <p className="klein leise">Wird geladen.</p>}
          </nav>

          <div className="handbuchleib" ref={leib}>
            {kapitel ? (
              <>
                <div className="reihe kopfzeile">
                  <h2 className="wachsen">{kapitel.titel}</h2>
                  {darfPflegen && (
                    <button className="knopf klein"
                            onClick={() => setBearbeite({ ...kapitel })}>
                      Ändern
                    </button>
                  )}
                </div>
                <Kapiteltext text={kapitel.text ?? ''} />
                <p className="klein leise" style={{ marginTop: 18 }}>
                  Zuletzt geändert am {datum(kapitel.updated_at)}.
                  {darfPflegen && ' Wer das Recht „Handbuch pflegen" hat, ändert '
                    + 'dieses Kapitel hier - dafür wird nichts ausgeliefert.'}
                </p>
              </>
            ) : (
              zeilen !== null && (
                <p className="klein leise">Es steht noch kein Kapitel darin.</p>
              )
            )}
          </div>
        </div>
      </div>

      {bearbeite && (
        <Kapitelfenster k={bearbeite} zu={() => setBearbeite(null)}
                        fertig={() => { setBearbeite(null); laden(); }} />
      )}
    </>
  );
}

/**
 * Ein Kapitel ändern.
 *
 * MIT VORSCHAU. Wer „## " tippt und nicht sieht, was daraus wird, tippt es
 * beim nächsten Mal nicht mehr - und dann steht das Handbuch wieder als
 * Fliesstext da.
 */
function Kapitelfenster(
  { k, zu, fertig }: { k: any; zu: () => void; fertig: () => void }
) {
  const [titel, setTitel] = useState(String(k.titel ?? ''));
  const [text, setText] = useState(String(k.text ?? ''));
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [zeige, setZeige] = useState(false);

  async function speichern() {
    setLaeuft(true); setFehler(null);
    try {
      await api.handbuchSetzen({
        schluessel: k.schluessel, titel, text,
        reihenfolge: k.reihenfolge,
        modul_schluessel: k.modul_schluessel ?? null,
      });
      fertig();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Kapitel ändern" untertitel={k.schluessel} breite={860} zu={zu}>
      <Fehlerkasten lage={fehler} />
      <Text name="titel" titel="Titel" wert={titel} setze={setTitel} />

      <div className="reihe" style={{ margin: '10px 0 6px' }}>
        <b className="wachsen">Text</b>
        <button className="knopf klein" onClick={() => setZeige(!zeige)}>
          {zeige ? 'Zurück zum Schreiben' : 'Vorschau'}
        </button>
      </div>

      {zeige ? (
        <div className="karte"><Kapiteltext text={text} /></div>
      ) : (
        <>
          <textarea className="feld" rows={20} value={text}
                    onChange={(e) => setText(e.target.value)}
                    style={{ width: '100%', fontFamily: 'inherit' }} />
          <p className="klein leise" style={{ marginTop: 6 }}>
            <b>„## "</b> macht eine Zwischenüberschrift, <b>„• "</b> einen
            Aufzählungspunkt, <b>„&gt; "</b> einen Merksatz. Ein Bild kommt in
            eine eigene Zeile als <code>[bild: datei.png | Unterschrift]</code>,
            ein Ablaufplan als <code>[ablauf: auftrag]</code>.
            {' '}<code>**so**</code> setzt ein Wort mitten im Satz fett - das
            ist die einzige Marke, die INNERHALB einer Zeile gilt. Sonst
            nichts - was keine Marke trägt, ist ein Absatz.
          </p>
        </>
      )}

      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft} onClick={speichern}>
          {laeuft ? 'Wird gespeichert' : 'Speichern'}
        </button>
        <button className="knopf" onClick={zu}>Abbrechen</button>
      </div>
    </Dialog>
  );
}
